import { useRef, useState } from 'react'
import { MultiFactorError, TotpSecret } from 'firebase/auth'
import { Errors } from 'config'
import type { FirebaseAuth } from 'shared/services'
import { LoadingStatus } from 'shared/types'
import { isTwoFactorEnabled } from 'shared/utils'
import { useAuthContext } from 'shared/components/AuthContext'
import type { MultiFactorChallenge } from 'shared/components/AuthContext'

export interface TwoFactorActionCallbacks {
  // Called after the status is set, so it may unmount the caller
  onDone?: () => Promise<void> | void
  onError?: (error: string | null) => void
}

interface RunOptions extends TwoFactorActionCallbacks {
  // Status set once the action has resolved
  doneStatus?: LoadingStatus
}

// Loading and Firebase error code of a 2FA call
const useTwoFactorAction = () => {
  const { auth } = useAuthContext()
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)

  const run = async (
    action: (auth: FirebaseAuth) => Promise<unknown>,
    { doneStatus = LoadingStatus.Succeeded, onDone, onError }: RunOptions = {}
  ) => {
    try {
      setLoading(LoadingStatus.Pending)
      setError(null)
      // eslint-disable-next-line no-throw-literal
      if (!auth) throw { code: Errors.NullUser }
      await action(auth)
      setLoading(doneStatus)
      await onDone?.()
    } catch (error: any) {
      const code = error?.code || null
      setError(code)
      setLoading(LoadingStatus.Failed)
      onError?.(code)
    }
  }

  return { loading, error, run }
}

// Turns a sign-in that needs the second factor into a challenge for the 2FA screen
export const useMultiFactorErrorHandler = () => {
  const { auth, setMultiFactorChallenge } = useAuthContext()

  return (
    error: unknown,
    challenge: Omit<MultiFactorChallenge, 'resolver'>
  ): boolean => {
    if (
      !auth ||
      (error as MultiFactorError)?.code !== Errors.MultiFactorAuthRequired
    )
      return false
    setMultiFactorChallenge?.({
      ...challenge,
      resolver: auth.getMultiFactorResolver(error as MultiFactorError),
    })
    return true
  }
}

export const useResolveMultiFactorSignIn = () => {
  const { multiFactorChallenge, setMultiFactorChallenge } = useAuthContext()
  const { loading, error, run } = useTwoFactorAction()
  // The challenge is cleared once verified, but the screen can stay a bit longer
  const emailRef = useRef(multiFactorChallenge?.email)
  if (multiFactorChallenge) emailRef.current = multiFactorChallenge.email

  const verify = async (code: string) => {
    if (!multiFactorChallenge) return
    const { resolver, onResolved } = multiFactorChallenge
    // After reauthentication onResolved navigates away and unmounts this screen,
    // so only the challenge (kept in the context) is cleared after it. It's cleared
    // even if onResolved fails - the user is signed in and the resolver is used up.
    await run(auth => auth.resolveTotpSignIn(resolver, code), {
      onDone: async () => {
        try {
          await onResolved()
        } finally {
          setMultiFactorChallenge?.(null)
        }
      },
    })
  }

  const cancel = () => setMultiFactorChallenge?.(null)

  return {
    email: emailRef.current,
    loading,
    error,
    verify,
    cancel,
  }
}

export const useIsTwoFactorEnabled = () => {
  const { auth } = useAuthContext()
  const getIsEnabled = () => isTwoFactorEnabled(auth?.getCurrentUser())
  const [isEnabled, setIsEnabled] = useState(getIsEnabled)
  const { loading, run } = useTwoFactorAction()

  const refresh = () => setIsEnabled(getIsEnabled())

  // 2FA may have been changed on another device since the page was opened
  const fetchIsEnabled = (
    onFetched: (isEnabled: boolean) => void,
    onError?: TwoFactorActionCallbacks['onError']
  ) =>
    run(auth => auth.reloadUserWithTokenRefresh(), {
      doneStatus: LoadingStatus.Idle,
      onDone: () => {
        const isEnabledNow = getIsEnabled()
        setIsEnabled(isEnabledNow)
        onFetched(isEnabledNow)
      },
      onError,
    })

  return {
    isEnabled,
    isFetching: loading === LoadingStatus.Pending,
    refresh,
    fetchIsEnabled,
  }
}

export const useEnrollTotp = () => {
  const [secret, setSecret] = useState<TotpSecret | null>(null)
  const { loading, error, run } = useTwoFactorAction()

  // Idle, not Succeeded - Succeeded means the factor has been enrolled
  const generateSecret = (callbacks?: TwoFactorActionCallbacks) =>
    run(async auth => setSecret(await auth.generateTotpSecret()), {
      ...callbacks,
      doneStatus: LoadingStatus.Idle,
    })

  const enroll = async (code: string, callbacks?: TwoFactorActionCallbacks) => {
    if (!secret) return
    await run(auth => auth.enrollTotp(secret, code), callbacks)
  }

  return {
    secret,
    loading,
    error,
    generateSecret,
    enroll,
  }
}

export const useUnenrollTotp = () => {
  const { loading, error, run } = useTwoFactorAction()

  const unenroll = (callbacks?: TwoFactorActionCallbacks) =>
    run(auth => auth.unenrollTotp(), callbacks)

  return {
    loading,
    error,
    unenroll,
  }
}

export const useCheckRevertSecondFactorCode = () => {
  const [email, setEmail] = useState<string | null>(null)
  const [factorDisplayName, setFactorDisplayName] = useState<string | null>(
    null
  )
  const { loading, error, run } = useTwoFactorAction()

  const checkRevertSecondFactorCode = (code: string) =>
    run(async auth => {
      const result = await auth.checkRevertSecondFactorCode(code)
      setEmail(result.email)
      setFactorDisplayName(result.factorDisplayName)
    })

  return {
    loading,
    error,
    email,
    factorDisplayName,
    checkRevertSecondFactorCode,
  }
}

export const useRevertSecondFactorAddition = () => {
  const { loading, error, run } = useTwoFactorAction()

  const revertSecondFactorAddition = (code: string) =>
    run(auth => auth.revertSecondFactorAddition(code))

  return {
    loading,
    error,
    revertSecondFactorAddition,
  }
}
